import axios from 'axios';
import cookie from 'js-cookie';

// Get the API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Service for managing closeout documents and folders
 * Handles API calls related to closeout docs operations
 */

// ============================================================================
// FOLDER OPERATIONS
// ============================================================================

/**
 * Create a new folder
 * @param {Object} folderData - { projectId, folderName, parentFolderId }
 * @returns {Promise<Object>} - Created folder DTO
 */
export const createFolder = async (folderData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/closeout-folders`, folderData, {
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error creating folder:', error);
        throw error;
    }
};

/**
 * Rename a folder
 * @param {string} folderId - The UUID of the folder to rename
 * @param {string} projectId - The UUID of the project (for permission check)
 * @param {string} newName - The new folder name
 * @returns {Promise<Object>} - Updated folder DTO
 */
export const renameFolder = async (folderId, projectId, newName) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/api/closeout-folders/${folderId}/rename?projectId=${projectId}`,
            { newName },
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error renaming folder:', error);
        throw error;
    }
};

/**
 * Delete a folder (soft delete, cascades to contents)
 * @param {string} folderId - The UUID of the folder to delete
 * @param {string} projectId - The UUID of the project (for permission check)
 * @returns {Promise<void>}
 */
export const deleteFolder = async (folderId, projectId) => {
    try {
        await axios.delete(
            `${API_BASE_URL}/api/closeout-folders/${folderId}?projectId=${projectId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
    } catch (error) {
        console.error('Error deleting folder:', error);
        throw error;
    }
};

/**
 * Move a folder to a new parent
 * @param {string} folderId - The UUID of the folder to move
 * @param {string} projectId - The UUID of the project (for permission check)
 * @param {string|null} newParentFolderId - The UUID of the new parent folder (null for root)
 * @returns {Promise<Object>} - Updated folder DTO
 */
export const moveFolder = async (folderId, projectId, newParentFolderId) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/api/closeout-folders/${folderId}/move?projectId=${projectId}`,
            { newParentFolderId },
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error moving folder:', error);
        throw error;
    }
};

/**
 * Get folder path for breadcrumbs
 * @param {string} folderId - The UUID of the folder
 * @param {string} projectId - The UUID of the project (for permission check)
 * @returns {Promise<Array>} - Array of folder path DTOs
 */
export const getFolderPath = async (folderId, projectId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/closeout-folders/${folderId}/path?projectId=${projectId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error getting folder path:', error);
        throw error;
    }
};

/**
 * Get hierarchical folder tree for a project
 * @param {string} projectId - The UUID of the project
 * @returns {Promise<Array>} - Array of folder tree DTOs with nested children
 */
export const getFolderTree = async (projectId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/closeout-folders/projects/${projectId}/tree`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error getting folder tree:', error);
        throw error;
    }
};

/**
 * Get folders in a project (optionally filtered by parent)
 * @param {string} projectId - The UUID of the project
 * @param {string|null} parentFolderId - The UUID of the parent folder (null for root)
 * @returns {Promise<Array>} - Array of folder DTOs
 */
export const getProjectFolders = async (projectId, parentFolderId = null) => {
    try {
        const url = parentFolderId
            ? `${API_BASE_URL}/api/closeout-folders/projects/${projectId}?parentFolderId=${parentFolderId}`
            : `${API_BASE_URL}/api/closeout-folders/projects/${projectId}`;

        const response = await axios.get(url, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting project folders:', error);
        throw error;
    }
};

/**
 * Generic search helper function to eliminate code duplication.
 * Validates query and makes search API request.
 *
 * @param {string} endpoint - The API endpoint to call
 * @param {string} projectId - The UUID of the project
 * @param {string} query - The search query string (2-100 chars)
 * @param {string} errorContext - Context for error messages
 * @returns {Promise<{data: Array, resultsLimited: boolean, totalResults: number}>}
 * @throws {Error} if query validation fails or API call fails
 */
const searchApi = async (endpoint, projectId, query, errorContext) => {
    // Validate query length
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 2) {
        throw new Error('Search query must be at least 2 characters');
    }

    if (trimmedQuery.length > 100) {
        throw new Error('Search query cannot exceed 100 characters');
    }

    try {
        const response = await axios.get(
            `${API_BASE_URL}${endpoint}?query=${encodeURIComponent(trimmedQuery)}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );

        // Extract metadata from response headers
        const resultsLimited = response.headers['x-results-limited'] === 'true';
        const totalResults = parseInt(response.headers['x-total-results'] || '0', 10);

        return {
            data: response.data,
            resultsLimited,
            totalResults,
        };
    } catch (error) {
        console.error(`Error ${errorContext}:`, error);
        throw error;
    }
};

/**
 * Search folders by name across entire project.
 * Returns top 100 results maximum.
 *
 * @param {string} projectId - The UUID of the project
 * @param {string} query - The search query string (2-100 chars)
 * @returns {Promise<{data: Array, resultsLimited: boolean, totalResults: number}>}
 */
export const searchFolders = async (projectId, query) => {
    return searchApi(
        `/api/closeout-folders/projects/${projectId}/search`,
        projectId,
        query,
        'searching folders'
    );
};

// ============================================================================
// DOCUMENT OPERATIONS
// ============================================================================

/**
 * Upload a document (multipart file upload)
 * @param {string} projectId - The UUID of the project
 * @param {File} file - The file to upload
 * @param {string|null} folderId - The UUID of the folder (null for root)
 * @returns {Promise<Object>} - Created document DTO
 */
export const uploadDocument = async (projectId, file, folderId = null) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('projectId', projectId);
        if (folderId) {
            formData.append('folderId', folderId);
        }

        const response = await axios.post(`${API_BASE_URL}/api/closeout-docs/upload`, formData, {
            withCredentials: true,
            headers: {
                'Content-Type': 'multipart/form-data',
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
    }
};

/**
 * Get documents in a folder (or root if folderId not provided)
 * @param {string} projectId - The UUID of the project
 * @param {string|null} folderId - The UUID of the folder (null for root)
 * @returns {Promise<Array>} - Array of document DTOs
 */
export const getDocuments = async (projectId, folderId = null) => {
    try {
        const url = folderId
            ? `${API_BASE_URL}/api/closeout-docs/project/${projectId}?folderId=${folderId}`
            : `${API_BASE_URL}/api/closeout-docs/project/${projectId}`;

        const response = await axios.get(url, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting documents:', error);
        throw error;
    }
};

/**
 * Search documents by filename across entire project.
 * Returns top 100 results maximum.
 *
 * @param {string} projectId - The UUID of the project
 * @param {string} query - The search query string (2-100 chars)
 * @returns {Promise<{data: Array, resultsLimited: boolean, totalResults: number}>}
 */
export const searchDocuments = async (projectId, query) => {
    return searchApi(
        `/api/closeout-docs/project/${projectId}/search`,
        projectId,
        query,
        'searching documents'
    );
};

/**
 * Get authenticated URL for viewing a document
 * Similar to how floorplan imageUrls work - returns URL that can be used in img/iframe
 * The authentication is handled via axios interceptors (withCredentials + XSRF-TOKEN)
 * @param {string} documentId - The UUID of the document
 * @param {string} projectId - The UUID of the project (for permission check)
 * @returns {string} - Authenticated URL for viewing the document
 */
export const getDocumentViewUrl = (documentId, projectId) => {
    return `${API_BASE_URL}/api/closeout-docs/${documentId}/download?projectId=${projectId}`;
};

/**
 * Download a document
 * @param {string} documentId - The UUID of the document
 * @param {string} projectId - The UUID of the project (for permission check)
 * @param {string} filename - The original filename for download
 * @returns {Promise<void>} - Triggers browser download
 */
export const downloadDocument = async (documentId, projectId, filename) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/closeout-docs/${documentId}/download?projectId=${projectId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
                responseType: 'blob', // Important for file download
            }
        );

        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading document:', error);
        throw error;
    }
};

/**
 * Rename a document
 * @param {string} documentId - The UUID of the document to rename
 * @param {string} projectId - The UUID of the project (for permission check)
 * @param {string} newFilename - The new filename
 * @returns {Promise<Object>} - Updated document DTO
 */
export const renameDocument = async (documentId, projectId, newFilename) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/api/closeout-docs/${documentId}/rename?projectId=${projectId}`,
            { newFilename },
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error renaming document:', error);
        throw error;
    }
};

/**
 * Move a document to a different folder
 * @param {string} documentId - The UUID of the document to move
 * @param {string} projectId - The UUID of the project (for permission check)
 * @param {string|null} newFolderId - The UUID of the new folder (null for root)
 * @returns {Promise<Object>} - Updated document DTO
 */
export const moveDocument = async (documentId, projectId, newFolderId) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/api/closeout-docs/${documentId}/move?projectId=${projectId}`,
            { newFolderId },
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error moving document:', error);
        throw error;
    }
};

/**
 * Delete a document (soft delete)
 * @param {string} documentId - The UUID of the document to delete
 * @param {string} projectId - The UUID of the project (for permission check)
 * @returns {Promise<void>}
 */
export const deleteDocument = async (documentId, projectId) => {
    try {
        await axios.delete(
            `${API_BASE_URL}/api/closeout-docs/${documentId}?projectId=${projectId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
    } catch (error) {
        console.error('Error deleting document:', error);
        throw error;
    }
};

// ============================================================================
// OWNER ACCESS OPERATIONS
// ============================================================================

// TODO: Re-enable these functions when backend PermissionsService is implemented
// These endpoints are currently disabled on the backend

/**
 * Get owner access status for a project
 * @param {string} projectId - The UUID of the project
 * @returns {Promise<Object>} - { enabled: boolean }
 */
// export const getOwnerAccessStatus = async (projectId) => {
//     try {
//         const response = await axios.get(
//             `${API_BASE_URL}/api/permissions/project/${projectId}/closeout-docs/owner-access`,
//             {
//                 withCredentials: true,
//                 headers: {
//                     'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
//                 },
//             }
//         );
//         return response.data;
//     } catch (error) {
//         console.error('Error getting owner access status:', error);
//         throw error;
//     }
// };

/**
 * Update owner access toggle for a project
 * @param {string} projectId - The UUID of the project
 * @param {boolean} enabled - Whether owner access is enabled
 * @returns {Promise<void>}
 */
// export const updateOwnerAccess = async (projectId, enabled) => {
//     try {
//         await axios.put(
//             `${API_BASE_URL}/api/permissions/project/${projectId}/closeout-docs/owner-access`,
//             { enabled },
//             {
//                 withCredentials: true,
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
//                 },
//             }
//         );
//     } catch (error) {
//         console.error('Error updating owner access:', error);
//         throw error;
//     }
// };

// ============================================================================
// HISTORY OPERATIONS
// ============================================================================

/**
 * Get complete revision history for a project's closeout docs
 * @param {string} projectId - The UUID of the project
 * @returns {Promise<Array>} - Array of CloseoutDocsHistoryDTO objects
 */
export const getProjectCloseoutDocsHistory = async (projectId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/closeout-docs-history/project/${projectId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error getting project closeout docs history:', error);
        throw error;
    }
};

/**
 * Get revision history for a specific folder
 * @param {string} folderId - The UUID of the folder
 * @returns {Promise<Array>} - Array of CloseoutDocsHistoryDTO objects
 */
export const getFolderHistory = async (folderId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/closeout-docs-history/folder/${folderId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error getting folder history:', error);
        throw error;
    }
};

/**
 * Get revision history for a specific document
 * @param {string} documentId - The UUID of the document
 * @returns {Promise<Array>} - Array of CloseoutDocsHistoryDTO objects
 */
export const getDocumentHistory = async (documentId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/closeout-docs-history/document/${documentId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error getting document history:', error);
        throw error;
    }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format change type for display
 * @param {string} changeType - The raw change type from API
 * @returns {string} User-friendly change type
 */
export const formatChangeType = (changeType) => {
    const changeTypeMap = {
        FOLDER_CREATED: 'Folder Created',
        FOLDER_UPDATED: 'Folder Updated',
        FOLDER_DELETED: 'Folder Deleted',
        DOCUMENT_UPLOADED: 'Document Uploaded',
        DOCUMENT_UPDATED: 'Document Updated',
        DOCUMENT_DELETED: 'Document Deleted',
    };

    return changeTypeMap[changeType] || changeType;
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// Note: File icon utility has been moved to utils/fileIconUtils.js
// This function returned icon name strings, but was never used.
// The actual implementation returns React components and is now centralized.
